import { useEffect, useMemo, useState } from '../../vendor/react';
import { getBusinessProfile } from '../../config/businessProfiles';
import { isSupabaseConfigured } from '../../config/runtime';
import type { Business, Branch } from '../auth/types';
import type { Product, ProductVariant, ProductWithInventory } from '../../types/domain';
import { getPublicCatalog } from '../production/cloud-api';
import { ensureCatalog, getCatalog, productWithInventory, subscribeCatalog } from './catalog-store';

type LocalDatabase = { workspaces?: Record<string, { businesses: Business[]; branches: Branch[] }> };
type RemoteInventory = { branchId: string; variantId?: string; onHand: number; reserved: number; inTransit: number };
type RemoteProduct = Product & { variants?: ProductVariant[]; inventory?: RemoteInventory[] };
type RemoteCatalog = { business: Business; branches: Branch[]; products: RemoteProduct[] };

function localPublicBusiness(slug: string) {
  try {
    const raw = localStorage.getItem('nexo.phase2.local.database');
    const database = raw ? JSON.parse(raw) as LocalDatabase : null;
    for (const workspace of Object.values(database?.workspaces ?? {})) {
      const business = workspace.businesses.find((item) => item.catalogSlug === slug || item.slug === slug);
      if (business) return { business, branches: workspace.branches.filter((item) => item.businessId === business.id) };
    }
  } catch { /* usa fallback local */ }
  const business: Business = {
    id: 'biz_casa_norte', organizationId: 'org_demo_norte', name: 'Casa Norte', slug: 'casa-norte', industry: 'Cafetería y alimentos', profileId: 'restaurant',
    phone: '899 100 2026', whatsapp: '528991002026', email: 'hola@casanorte.demo', address: 'Calle Hidalgo 120', city: 'Reynosa', state: 'Tamaulipas', country: 'México', postalCode: '88500', currency: 'MXN', timezone: 'America/Monterrey', primaryColor: '#163d36', accentColor: '#e49b5d', catalogEnabled: true, catalogSlug: 'casa-norte', catalogShowPrices: true, catalogShowAvailability: true, catalogAllowOrders: true, catalogAllowPickup: true, catalogAllowDelivery: true, catalogHideOutOfStock: false, catalogShowExactStock: false,
  };
  return { business, branches: [
    { id: 'br_centro', businessId: business.id, name: 'Sucursal Centro', address: business.address, phone: business.phone, schedule: 'Lun–Sáb, 8:00–20:00', registerName: 'Caja principal', warehouseName: 'Almacén Centro' },
    { id: 'br_fuentes', businessId: business.id, name: 'Sucursal Las Fuentes', address: 'Av. Las Fuentes 450', phone: business.phone, schedule: 'Lun–Dom, 9:00–21:00', registerName: 'Caja principal', warehouseName: 'Almacén Las Fuentes' },
  ] };
}

function remoteProducts(catalog: RemoteCatalog, branchId?: string): ProductWithInventory[] {
  return catalog.products.map((item) => {
    const { variants = [], inventory = [], ...product } = item;
    const row = inventory.find((value) => value.branchId === branchId && !value.variantId);
    const stock = Number(row?.onHand ?? 0);
    const reserved = Number(row?.reserved ?? 0);
    return {
      ...product,
      businessId: catalog.business.id,
      priceCents: Number(product.priceCents ?? 0),
      wholesalePriceCents: Number(product.wholesalePriceCents ?? 0),
      costCents: Number(product.costCents ?? 0),
      taxRate: Number(product.taxRate ?? 0),
      minStock: Number(product.minStock ?? 0),
      maxStock: Number(product.maxStock ?? 0),
      stock,
      reserved,
      inTransit: Number(row?.inTransit ?? 0),
      available: Math.max(0, stock - reserved),
      variants: variants.map((variant) => ({ ...variant, productId: product.id, priceCents: Number(variant.priceCents ?? 0), costCents: Number(variant.costCents ?? 0) })),
    };
  });
}

export function usePublicCatalog(slug: string, selectedBranchId?: string) {
  const [revision, setRevision] = useState(0);
  const [remote, setRemote] = useState<RemoteCatalog | null | 'loading'>('loading');
  const [remoteError, setRemoteError] = useState('');
  useEffect(() => subscribeCatalog(() => setRevision((value) => value + 1)), []);
  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let active = true;
    setRemote('loading');
    setRemoteError('');
    void getPublicCatalog(slug)
      .then((payload) => { if (active) setRemote(payload as RemoteCatalog | null); })
      .catch((error) => { if (active) { setRemote(null); setRemoteError(error instanceof Error ? error.message : 'No se pudo consultar Supabase.'); } });
    return () => { active = false; };
  }, [slug]);

  const local = useMemo(() => localPublicBusiness(slug), [slug, revision]);
  const remoteCatalog = remote && remote !== 'loading' ? remote : null;
  const resolved = remoteCatalog ?? local;
  const branch = resolved.branches.find((item) => item.id === selectedBranchId) ?? resolved.branches[0];
  const profile = getBusinessProfile(resolved.business.profileId);
  const localCatalog = getCatalog(resolved.business.id) ?? ensureCatalog(resolved.business.id, resolved.branches.map((item) => item.id), profile.id);
  const products = remoteCatalog ? remoteProducts(remoteCatalog, branch?.id) : (branch ? productWithInventory(localCatalog, branch.id) : []);
  return {
    business: resolved.business,
    branches: resolved.branches,
    branch,
    profile,
    catalog: localCatalog,
    products,
    loading: isSupabaseConfigured && remote === 'loading',
    notFound: isSupabaseConfigured && remote === null && !remoteError,
    error: remoteError,
  };
}
